// src/pages/seller/OverviewPage.jsx
import React, { useEffect, useState } from 'react';
import { 
  TrendingUp, Package, ShoppingBag, Eye, Truck, FileText, 
  MessageCircle, Star 
} from 'lucide-react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useApp } from '../../context/AppContext';

const OverviewPage = () => {
  const { user } = useApp();
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeOrders: 0,
    totalRevenue: 0,
    productViews: 0,
    shippedOrders: 0,
    pendingInquiries: 0,
    unreadMessages: 0,
    totalReviews: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (user?.status === 'pending' || !user?.uid) return;

    const cleanups = [];

    // 1. Products
    const productsQuery = query(collection(db, 'products'), where('sellerId', '==', user.uid));
    const unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
      setStats(prev => ({ ...prev, totalProducts: snapshot.size }));
    });
    cleanups.push(unsubscribeProducts);

    // 2. Orders
    const ordersQuery = query(collection(db, 'orders'), where('sellerId', '==', user.uid));
    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      const orders = snapshot.docs.map(doc => doc.data());
      const activeOrders = orders.filter(order => 
        ['confirmed', 'processing', 'shipped'].includes(order.status)
      ).length;
      const shippedOrders = orders.filter(order => order.status === 'delivered').length;
      setStats(prev => ({ ...prev, activeOrders, shippedOrders }));
    });
    cleanups.push(unsubscribeOrders);

    // 3. Messages (Inbox - unread count)
    const messagesQuery = query(collection(db, 'messages'), where('sellerId', '==', user.uid));
    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      let unread = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        unread += data.unreadCount || 0;
      });
      setStats(prev => ({ ...prev, unreadMessages: unread }));
    });
    cleanups.push(unsubscribeMessages);

    // 4. Reviews
    const productsForReviewsQuery = query(collection(db, 'products'), where('sellerId', '==', user.uid));
    const unsubscribeProductsForReviews = onSnapshot(productsForReviewsQuery, (productSnap) => {
      const productIds = productSnap.docs.map(d => d.id);
      if (productIds.length === 0) {
        setStats(prev => ({ ...prev, totalReviews: 0 }));
        return;
      }

      const commentsQuery = query(collection(db, 'comments'), where('productId', 'in', productIds));
      const unsubscribeComments = onSnapshot(commentsQuery, (commentSnap) => {
        setStats(prev => ({ ...prev, totalReviews: commentSnap.size }));
      });
      cleanups.push(unsubscribeComments);
    });
    cleanups.push(unsubscribeProductsForReviews);

    // Mock static data (revenue, views, inquiries)
    setStats(prev => ({
      ...prev,
      totalRevenue: 12450,
      productViews: 1240,
      pendingInquiries: 3 // 또는 실제 inquiry 시스템 연결 시 제거
    }));

    // Recent Activity — 실제 시스템이라면 여기에도 messages/reviews 포함 가능
    setRecentActivity([
      { type: 'order', id: 'ORD-2025-001', buyer: 'ABC Construction', amount: '$2,450', date: '2025-04-01' },
      { type: 'inquiry', id: 'INQ-2025-003', buyer: 'XYZ Builders', message: 'MOQ negotiation', date: '2025-04-02' },
      { type: 'order', id: 'ORD-2025-002', buyer: 'Global Interiors', amount: '$1,800', date: '2025-04-03' }
    ]);

    return () => {
      cleanups.forEach(unsub => unsub && unsub());
    };
  }, [user?.uid, user?.status]);

  if (user?.status === 'pending') {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <div className="text-center">
          <h2 className="text-3xl font-light tracking-tight text-gray-900 mb-4">Account Pending Approval</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Your seller account is currently under review.
          </p>
          <div className="bg-yellow-50 p-4 rounded-lg inline-block">
            <p className="text-yellow-800 font-medium">Estimated approval time: 1-2 business days</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ 값이 0이면 "None" 또는 "No data"로 표시
  const formatStatValue = (value, isCurrency = false) => {
    if (value === 0 || value === null || value === undefined) {
      return 'None';
    }
    if (isCurrency) {
      return `$${value.toLocaleString()}`;
    }
    return value.toLocaleString();
  };

  const kpiData = [
    { title: 'Total Products', value: stats.totalProducts, icon: Package, format: false },
    { title: 'Active Orders', value: stats.activeOrders, icon: ShoppingBag, format: false },
    { title: 'Shipped Orders', value: stats.shippedOrders, icon: Truck, format: false },
    { title: 'Total Revenue', value: stats.totalRevenue, icon: TrendingUp, format: true },
    { title: 'Product Views', value: stats.productViews, icon: Eye, format: false },
    { title: 'Pending Inquiries', value: stats.pendingInquiries, icon: FileText, format: false },
    { title: 'Unread Messages', value: stats.unreadMessages, icon: MessageCircle, format: false },
    { title: 'Customer Reviews', value: stats.totalReviews, icon: Star, format: false }
  ];

  return (
    <div>
      <h1 className="text-3xl font-light tracking-tight text-gray-900 mb-8">Seller Dashboard Overview</h1>
      
      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {kpiData.map((item, index) => {
          const Icon = item.icon;
          let bgColor = 'bg-gray-100';
          let iconColor = 'text-gray-600';

          if (item.icon === MessageCircle) {
            bgColor = 'bg-blue-100';
            iconColor = 'text-blue-600';
          } else if (item.icon === Star) {
            bgColor = 'bg-yellow-100';
            iconColor = 'text-yellow-600';
          }

          const displayValue = formatStatValue(item.value, item.format);

          return (
            <div key={index} className="p-5 border border-gray-200 rounded-xl bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{item.title}</p>
                  <p className="text-2xl font-bold mt-1 text-gray-900">
                    {displayValue}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${bgColor}`}>
                  <Icon size={24} className={iconColor} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
        {recentActivity.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            You haven't received any orders, messages, or reviews yet.
          </p>
        ) : (
          <div className="space-y-4">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex justify-between items-center p-3 border-b border-gray-100 last:border-b-0">
                <div>
                  <p className="font-medium text-gray-900">{item.buyer}</p>
                  <p className="text-sm text-gray-600">
                    {item.type === 'order' ? `Order #${item.id}` : `Inquiry #${item.id}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{item.amount || item.message}</p>
                  <p className="text-sm text-gray-500">{item.date}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl">
          <h3 className="font-medium text-blue-800 mb-2">Order Management</h3>
          <p className="text-blue-700 text-sm mb-3">Check and process new orders</p>
          <a href="/seller/orders" className="text-blue-600 text-sm font-medium hover:underline">
            Go to Orders →
          </a>
        </div>
        <div className="bg-green-50 p-6 rounded-xl">
          <h3 className="font-medium text-green-800 mb-2">Product Management</h3>
          <p className="text-green-700 text-sm mb-3">Add or update your products</p>
          <a href="/seller/products" className="text-green-600 text-sm font-medium hover:underline">
            Manage Products →
          </a>
        </div>
        <div className="bg-purple-50 p-6 rounded-xl">
          <h3 className="font-medium text-purple-800 mb-2">Inbox</h3>
          <p className="text-purple-700 text-sm mb-3">Respond to customer messages</p>
          <a href="/seller/messages" className="text-purple-600 text-sm font-medium hover:underline">
            View Messages →
          </a>
        </div>
        <div className="bg-amber-50 p-6 rounded-xl">
          <h3 className="font-medium text-amber-800 mb-2">Customer Reviews</h3>
          <p className="text-amber-700 text-sm mb-3">See what buyers say about you</p>
          <a href="/seller/reviews" className="text-amber-600 text-sm font-medium hover:underline">
            View Reviews →
          </a>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;